import { UnifiedMonitoringProvider } from '@hakimo-ui/hakimo/providers';
import { WsConnectionStatus } from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import {
  useAuthUtils,
  useUnifiedMonitoringWs,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert } from '@hakimo-ui/shared/ui-base';
import { ReactNode, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import UnifiedMonitoringContainer from './UnifiedMonitoringContainer';

const UnifiedMonitoringRootComponent = () => {
  const { getAccessToken } = useAuthUtils();

  const wsUrl = useMemo(() => {
    const host = window.location.hostname;
    if (host === 'localhost') {
      const port = window.location.port;
      return `ws://${host}:${port}/monitoring`;
    }
    return `wss://event-flow-${host}/monitoring`;
  }, []);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <UnifiedMonitoringProvider
            url={wsUrl}
            getAccessToken={getAccessToken}
          >
            <WsHandler>
              <UnifiedMonitoringContainer />
            </WsHandler>
          </UnifiedMonitoringProvider>
        }
      />
    </Routes>
  );
};

export const UnifiedMonitoringRoot = withAuthz(
  withErrorBoundary(UnifiedMonitoringRootComponent),
  ['unified_monitoring:view']
);

interface WsHandlerProps {
  children: ReactNode;
}

function WsHandler(props: WsHandlerProps) {
  const {
    connectionState: { status, nextRetryIn },
  } = useUnifiedMonitoringWs();
  switch (status) {
    case WsConnectionStatus.PENDING:
      return (
        <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg z-10 flex h-full w-full items-center justify-center">
          <LoadingIndicator text="Connecting..." />
        </div>
      );
    case WsConnectionStatus.CLOSED:
      return (
        <Alert type="error">
          Connection terminated. This may be due to a connection error or login
          from another location. Refresh to use Monitoring in this window.
        </Alert>
      );
    case WsConnectionStatus.ERROR:
      return (
        <Alert type="error">
          An error occurred while connecting to monitoring. Please try again
          later.
        </Alert>
      );
    case WsConnectionStatus.RECONNECTING:
    case WsConnectionStatus.CONNECTED:
      return (
        <>
          {status === WsConnectionStatus.RECONNECTING && (
            <Alert type="info">
              Unable to connect. Trying again in {nextRetryIn} seconds...
            </Alert>
          )}
          {props.children}
        </>
      );

    default:
      return null;
  }
}
